/**
 * mm-cart.js
 *
 * Three custom elements:
 *   <mm-cart-remove-button>  remove one line
 *   <mm-quantity-input>      the stepper
 *   <mm-cart-items>          the drawer's state machine
 *
 * See docs/MM-CART-DRAWER-SPEC.md §5.4 and §5.5.
 */
(function () {
  const PUB_SUB_EVENTS = theme.pubsub.PUB_SUB_EVENTS;
  const MM_QUANTITY_DEBOUNCE_MS = 300;
  const REWARD_TOAST_MS = 3500;

  // Horizon dispatches this on every add-to-cart surface it owns (PDP, quick
  // add, featured product...). The stock cart drawer auto-opened off it, so
  // this is the contract to honour rather than relying on the fetch shim.
  const THEME_CART_LINES_UPDATE = 'shopify:cart:lines-update';

  // Contract with assets/mmx-product-information.js: a quantity tier bought with
  // a discount code stamps the code on its line as this hidden property, and
  // lists it in this cart attribute. Together they say which of the cart's codes
  // this theme applied on the shopper's behalf — so `reconcileTierDiscounts` can
  // drop one when its line goes without touching a code the shopper typed or the
  // rewards bar granted. Change these names in both files or not at all.
  const TIER_DISCOUNT_PROPERTY = '_mmx_tier_discount';
  const TIER_DISCOUNT_ATTRIBUTE = '_mmx_tier_discounts';

  const routes = (window.theme && theme.routes) || {};
  const cartChangeUrl = routes.cart_change_url || '/cart/change';
  const cartUpdateUrl = routes.cart_update_url || '/cart/update';
  const cartUrl = routes.cart_url || '/cart';

  /* ---------------------------------------------------------------- remove */

  class MMCartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.closest('mm-cart-items')?.updateQuantity(this.dataset.index, 0, null, this);
      });
    }
  }

  /* -------------------------------------------------------------- quantity */

  class MMQuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });

      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick)
      );
    }

    connectedCallback() {
      this.validateQtyRules();
      this.unsubscribe = theme.pubsub.subscribe(PUB_SUB_EVENTS.quantityUpdate, () =>
        this.validateQtyRules()
      );
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      document.removeEventListener(THEME_CART_LINES_UPDATE, this.onThemeCartLinesUpdate);
    }

    onButtonClick = (event) => {
      event.preventDefault();
      if (!this.input) return;

      const previous = this.input.value;
      const min = parseInt(this.input.dataset.min || '1', 10);
      const step = parseInt(this.input.step || '1', 10);

      if (event.currentTarget.name === 'plus') {
        // A line sitting at 0 with a minimum above the step jumps straight to it.
        if (parseInt(this.input.value, 10) === 0 && min > step) {
          this.input.value = String(min);
        } else {
          this.input.stepUp();
        }
      } else {
        this.input.stepDown();
      }

      if (previous !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    };

    validateQtyRules() {
      if (!this.input) return;

      const value = parseInt(this.input.value, 10);
      const buttons = this.querySelectorAll('button');
      const minus = buttons[0];
      const plus = buttons[1];

      if (this.input.min) {
        const min = parseInt(this.input.min, 10);
        minus?.classList.toggle('disabled', value <= min);
      }

      if (this.input.max) {
        const max = parseInt(this.input.max, 10);
        plus?.classList.toggle('disabled', value >= max);
      }
    }
  }

  /* ----------------------------------------------------------- cart items */

  class MMCartItems extends HTMLElement {
    constructor() {
      super();

      this.reachedRewardKeys = new Set();
      this.pendingPromoAdds = new Set();
      this.pendingDiscountCodes = new Set();
      this.appliedRewardDiscounts = new Set();
      this._quantityTimers = new Map();
      this._postCartUpdateRAF = null;
      this._latestCart = null;
      this._announceNext = false;
      this._cartJSONStarted = false;

      /**
       * Every listener this element binds is scoped to this controller.
       *
       * <mm-cart-drawer> reparents itself to <body> on its first connect, which
       * disconnects and reconnects the whole subtree — so connectedCallback here
       * runs twice on every page load. The upsell and set-promo handlers sit on
       * the *panel*, which travels with the drawer, so a second run used to leave
       * two live click listeners on it: one tap ran two adds in the same tick and
       * `mergeItems` in mm-cart-intercept.js collapsed them into quantity 2.
       *
       * @type {AbortController}
       */
      this._listeners = new AbortController();
    }

    connectedCallback() {
      this._listeners = new AbortController();
      this.init();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.unsubscribe = null;
      this._listeners.abort();
    }

    init() {
      const { signal } = this._listeners;

      this.addEventListener('change', this.onChange, { signal });
      this.unsubscribe = theme.pubsub.subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'mm-cart-items') return;
        this.onCartUpdate(event);
      });

      document.addEventListener(THEME_CART_LINES_UPDATE, this.onThemeCartLinesUpdate, { signal });

      this.setupUpsell();
      this.setupSetPromo();
      this.initCartJSON();
    }

    /**
     * A theme-native add went through its own request, so this drawer knows
     * nothing about it. Re-render the section from the server, then open.
     */
    onThemeCartLinesUpdate = (event) => {
      if (event?.detail?.source === 'mm-cart-drawer') return;

      const action = event?.action ?? event?.detail?.action;
      const isAdd = action === 'add';
      const drawer = document.querySelector('mm-cart-drawer');

      // The event fires before the request resolves, so open straight away the
      // way the stock drawer did rather than making the shopper wait on a round
      // trip. The exception is an empty drawer: opening it now would flash the
      // empty state before the new line paints, so that one waits.
      const openedEarly = isAdd && drawer && !drawer.classList.contains('is-empty');
      if (openedEarly) {
        if (drawer.classList.contains('active')) drawer.classList.add('is-busy');
        else drawer.show();
      }

      const settle = () =>
        this.refreshFromServer().finally(() => {
          drawer?.classList.remove('is-busy');
          if (isAdd) drawer?.show();
        });

      if (event?.promise?.then) {
        event.promise
          .then((result) => {
            if (result?.detail?.didError) {
              drawer?.classList.remove('is-busy');
              return;
            }
            return settle();
          })
          .catch(settle);
      } else {
        settle();
      }
    };

    /** Re-renders the drawer's own section through the Section Rendering API. */
    refreshFromServer() {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', this.sectionId);

      // Fired together rather than chained — the markup and the cart JSON do not
      // depend on each other, so this costs one round trip, not two.
      return Promise.all([
        // No Accept header: asking a collection URL for application/json makes
        // it 404. The `sections` param alone is what selects the JSON response.
        fetch(url.toString(), { cache: 'no-store' })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null),
        this.getCart(true),
      ])
        .then(([sections, cart]) => {
          if (sections) this.renderSections(sections);
          this.schedulePostCartUpdateWork(true, cart);
          return cart;
        })
        .catch((error) => {
          console.error('[mm-cart] section refresh failed', error);
        });
    }

    onChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target.classList.contains('js-subscribe-toggle')) {
        this.onItemSubscribeToggle(target);
        return;
      }

      if (target.classList.contains('mm-quantity__input')) {
        this.validateQuantity(event);
      }
    };

    /**
     * The progress bar and upsell live inside a closed drawer, so their first
     * /cart.js read is not part of first paint. Run it on idle, or as soon as
     * the drawer opens — whichever comes first.
     */
    initCartJSON() {
      const run = () => {
        if (this._cartJSONStarted) return;
        this._cartJSONStarted = true;

        this.getCart().then((cart) => {
          if (!cart) return;
          this._latestCart = cart;
          this.updateSetPromoVisibility(cart);
          this.filterUpsellProducts(cart);
          this.updateProgressBar(cart, false);
        });
      };

      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
      setTimeout(run, 1000);

      document.addEventListener('mm-cart-drawer:open', run, {
        once: true,
        signal: this._listeners.signal,
      });
    }

    getCart(force = false) {
      if (!force && this._cartPromise) return this._cartPromise;

      this._cartPromise = fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)
        .finally(() => {
          // Allow the next call to refetch.
          setTimeout(() => {
            this._cartPromise = null;
          }, 0);
        });

      return this._cartPromise;
    }

    /* ------------------------------------------------------------ sections */

    get sectionId() {
      return document.querySelector('#CartDrawer')?.dataset.sectionId || 'mm-cart-drawer';
    }

    getSectionsToRender() {
      const section = this.sectionId;
      return [
        { id: 'CartDrawer', section, selector: 'mm-cart-items' },
        { id: 'CartDrawer', section, selector: '#main-cart-drawer-footer' },
        { id: 'CartDrawer', section, selector: '.mm-drawer__header' },
      ];
    }

    /**
     * Parses the section payload once and reads every region out of that single
     * document. Writing innerHTML only when it differs preserves focus, scroll
     * position and in-flight transitions.
     */
    renderSections(sections) {
      if (!sections) return;

      const html = sections[this.sectionId] ?? sections['mm-cart-drawer'];
      if (!html) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');

      this.getSectionsToRender().forEach(({ selector }) => {
        const source = parsed.querySelector(selector);
        const destination = document.querySelector(`#CartDrawer ${selector}`);
        if (!source || !destination) return;
        if (source.innerHTML === destination.innerHTML) return;

        destination.innerHTML = source.innerHTML;
      });

      this.syncEmptyState();
    }

    syncEmptyState() {
      const drawer = document.querySelector('mm-cart-drawer');
      if (!drawer) return;

      const hasItems = Boolean(document.querySelector('#CartDrawer .mm-cart-item'));
      drawer.classList.toggle('is-empty', !hasItems);

      if (!hasItems) this.addEmptyState();
    }

    /**
     * The empty state is rendered server-side when the cart starts empty. When
     * the last line is removed there is no server render to lean on, so build it.
     */
    addEmptyState() {
      const inner = document.querySelector('#CartDrawer .drawer__inner');
      if (!inner || inner.querySelector('.drawer__inner-empty')) return;

      const empty = document.createElement('div');
      empty.className = 'drawer__inner-empty';
      empty.innerHTML = `
        <button class="mm-drawer__close" type="button" aria-label="Close"
                onclick="this.closest('mm-cart-drawer').close()">
          ${document.querySelector('#CartDrawer .mm-drawer__close')?.innerHTML ?? '&times;'}
        </button>
        <p class="mm-drawer__empty-title">Your cart is empty</p>
        <a class="mm-drawer__empty-link" href="${routes.root_url || '/'}">Continue shopping</a>
      `;

      inner.prepend(empty);
    }

    /* ------------------------------------------------------------ mutation */

    markLinePending(line, isPending) {
      const row = document.querySelector(`#CartDrawer-Item-${line}`);
      row?.classList.toggle('is-pending', Boolean(isPending));
    }

    enableLoading(line, isRemoval) {
      const row = document.querySelector(`#CartDrawer-Item-${line}`);
      if (!row) return;

      // A quantity change already shows the number the shopper picked, so only
      // a removal (which has no other feedback) gets a spinner.
      if (isRemoval) row.querySelector('.cart-loader')?.classList.remove('hidden');
    }

    disableLoading(line) {
      const row = document.querySelector(`#CartDrawer-Item-${line}`);
      row?.querySelector('.cart-loader')?.classList.add('hidden');
      row?.querySelector('.ci-subscribe__loader')?.classList.add('hidden');
    }

    /** Lines are addressed by key so two in-flight updates cannot cross. */
    lineIdentifier(line) {
      const row = document.querySelector(`#CartDrawer-Item-${line}`);
      const key = row?.dataset.key;
      return key ? { id: key } : { line: Number(line) };
    }

    validateQuantity(event) {
      const input = event.target;
      const line = input.dataset.index;
      const quantity = parseInt(input.value, 10);

      if (Number.isNaN(quantity)) return;

      this.markLinePending(line, true);

      // One timer per line — a global timer lets the second line's change
      // swallow the first's.
      clearTimeout(this._quantityTimers.get(line));
      this._quantityTimers.set(
        line,
        setTimeout(() => {
          this._quantityTimers.delete(line);
          this.updateQuantity(line, quantity, input.name, input);
        }, MM_QUANTITY_DEBOUNCE_MS)
      );
    }

    updateQuantity(line, quantity, name, target) {
      const isRemoval = Number(quantity) === 0;

      this.markLinePending(line, true);
      this.enableLoading(line, isRemoval);

      const body = JSON.stringify({
        ...this.lineIdentifier(line),
        quantity: Number(quantity),
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      return fetch(`${cartChangeUrl}.js`, { ...theme.utils.fetchConfig(), body })
        .then((response) => response.text())
        .then((text) => {
          const parsed = JSON.parse(text);

          if (parsed.errors) {
            this.showLineError(line, parsed.errors);
            return;
          }

          this.renderSections(parsed.sections);
          this._latestCart = parsed;

          theme.pubsub.publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'mm-cart-items',
            cartData: parsed,
          });
          theme.pubsub.publish(PUB_SUB_EVENTS.quantityUpdate, undefined);

          this.schedulePostCartUpdateWork(true, parsed);
          this.updateLiveRegions(line, parsed);
        })
        .catch((error) => {
          console.error('[mm-cart] quantity update failed', error);
          this.showCartError();
        })
        .finally(() => {
          this.disableLoading(line);
          this.markLinePending(line, false);
        });
    }

    onCartUpdate(event) {
      const cart = event?.cartData;
      if (cart?.sections) this.renderSections(cart.sections);
      this.schedulePostCartUpdateWork(false, cart);
    }

    /* ----------------------------------------------------------- subscribe */

    resolveSellingPlanIdFromEl(row) {
      if (!row) return '';

      if (row.dataset.firstSellingPlanId) return row.dataset.firstSellingPlanId;

      const raw = row.dataset.sellingPlans;
      if (!raw) return '';

      let plans;
      try {
        plans = JSON.parse(raw);
      } catch (error) {
        return '';
      }
      if (!Array.isArray(plans) || plans.length === 0) return '';

      const frequency = this.dataset.defaultFrequency || 'first';
      if (frequency !== 'first') {
        const match = plans.find((plan) => String(plan.name || '').includes(frequency));
        if (match) return match.id;
      }

      return plans[0].id;
    }

    onItemSubscribeToggle(toggle) {
      const line = toggle.dataset.index;
      const row = document.querySelector(`#CartDrawer-Item-${line}`);
      const input = row?.querySelector('.mm-quantity__input');
      const quantity = input ? parseInt(input.value, 10) : 1;
      const wasChecked = toggle.checked;

      const sellingPlan = wasChecked ? this.resolveSellingPlanIdFromEl(row) : '';

      toggle.disabled = true;
      row?.querySelector('.ci-subscribe__loader')?.classList.remove('hidden');

      const body = JSON.stringify({
        ...this.lineIdentifier(line),
        quantity,
        selling_plan: sellingPlan,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      });

      fetch(`${cartChangeUrl}.js`, { ...theme.utils.fetchConfig(), body })
        .then((response) => response.json())
        .then((parsed) => {
          if (parsed.errors) throw new Error(parsed.errors);

          this.renderSections(parsed.sections);
          this._latestCart = parsed;
          this.schedulePostCartUpdateWork(false, parsed);
        })
        .catch((error) => {
          console.error('[mm-cart] subscribe toggle failed', error);
          toggle.checked = !wasChecked;
          this.showCartError();
        })
        .finally(() => {
          toggle.disabled = false;
          this.disableLoading(line);
        });
    }

    /* ------------------------------------------------------- post-update */

    schedulePostCartUpdateWork(announce, cart) {
      this._announceNext = this._announceNext || Boolean(announce);
      if (cart) this._latestCart = cart;

      if (this._postCartUpdateRAF) return;

      // Coalesced on a macrotask rather than a frame. requestAnimationFrame
      // never fires while the browser isn't painting the tab, which silently
      // left the progress bar, upsell and set promo showing a stale cart.
      this._postCartUpdateRAF = setTimeout(() => {
        this._postCartUpdateRAF = null;

        const shouldAnnounce = this._announceNext;
        this._announceNext = false;

        const latest = this._latestCart;

        this.notifyHeaderCart(latest);
        this.updateSetPromoVisibility(latest);
        this.filterUpsellProducts(latest);
        this.updateProgressBar(latest, shouldAnnounce);
        this.reconcileTierDiscounts(latest);
      }, 0);
    }

    notifyHeaderCart(cart) {
      const itemCount =
        cart?.item_count ??
        parseInt(document.querySelector('#CartDrawer .drawer__heading-amount')?.textContent || '0', 10);

      // The theme's <cart-icon> only listens for its own Shopify event, so the
      // bubble is updated directly rather than through a round trip.
      const icon = document.querySelector('cart-icon');
      if (icon?.renderCartBubble) icon.renderCartBubble(itemCount, false);

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          detail: { resource: cart, data: { itemCount, source: 'mm-cart-drawer' } },
        })
      );
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }

    /* ------------------------------------------------------------- errors */

    showCartError(message) {
      const target = document.querySelector('#CartDrawer-CartErrors');
      if (target) target.textContent = message || 'Something went wrong. Please try again.';
    }

    showLineError(line, message) {
      const target = document.querySelector(`#CartDrawer-LineItemError-${line}`);
      if (target) target.textContent = message;
    }

    updateLiveRegions(line, cart) {
      const status = document.querySelector('#CartDrawer-LiveRegionText');
      if (status && cart) status.textContent = `Cart updated. ${cart.item_count} items.`;
    }

    /* -------------------------------------------------------- set promo */

    setupSetPromo() {
      const panel = this.closest('.mm-cart-drawer__panel') || document;

      panel.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest?.('.js-set-promo-atc');
          if (!button) return;

          event.preventDefault();
          this.handleSetPromo(button);
        },
        { signal: this._listeners.signal }
      );
    }

    handleSetPromo(button) {
      const variantId = Number(button.dataset.id);
      if (!variantId) return;

      // One add per press: the engine batches everything queued in a tick into a
      // single line, so a second dispatch would land as quantity 2.
      if (button.getAttribute('aria-busy') === 'true') return;

      button.setAttribute('aria-busy', 'true');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');

      this.addToCart([{ id: variantId, quantity: 1 }]).finally(() => {
        button.removeAttribute('aria-busy');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      });
    }

    updateSetPromoVisibility(cart) {
      const promo = document.querySelector('#CartDrawer-SetPromo');
      if (!promo || !cart) return;

      // A link-only banner carries no product, so there is nothing to hide it against.
      if (!promo.dataset.promoProductId) return;

      const productId = Number(promo.dataset.promoProductId);
      const inCart = (cart.items || []).some((item) => item.product_id === productId);
      promo.classList.toggle('is-hidden', inCart);
    }

    /* ----------------------------------------------------------- upsell */

    setupUpsell() {
      // The upsell block is a sibling of this element inside .drawer__body, so
      // the listener has to sit on the panel rather than on `this`.
      const panel = this.closest('.mm-cart-drawer__panel') || document;

      const { signal } = this._listeners;

      panel.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest?.('.js-upsell-atc');
          if (!button) return;

          event.preventDefault();
          this.handleUpsellAdd(button);
        },
        { signal }
      );

      panel.addEventListener(
        'change',
        (event) => {
          const select = event.target.closest?.('.js-upsell-variant-select');
          if (select) this.onUpsellVariantChange(select);
        },
        { signal }
      );

      this.setupUpsellSlider(panel, signal);
    }

    /* ------------------------------------------------------ upsell slider */

    /**
     * The cards scroll sideways past the second one. Everything about the strip
     * is CSS; this only drives the arrows and hides them while it all fits,
     * which changes as `filterUpsellProducts` hides what is already in the cart.
     */
    setupUpsellSlider(panel, signal) {
      const track = panel.querySelector?.('[data-upsell-track]');
      if (!track) return;

      panel.addEventListener(
        'click',
        (event) => {
          const arrow = event.target.closest?.('[data-upsell-prev], [data-upsell-next]');
          if (!arrow) return;

          event.preventDefault();
          const direction = arrow.hasAttribute('data-upsell-next') ? 1 : -1;
          track.scrollBy({ left: direction * this.upsellPageWidth(track), behavior: 'smooth' });
        },
        { signal }
      );

      track.addEventListener('scroll', () => this.updateUpsellControls(), { signal, passive: true });

      if ('ResizeObserver' in window) {
        this._upsellResize = new ResizeObserver(() => this.updateUpsellControls());
        this._upsellResize.observe(track);
        signal.addEventListener('abort', () => this._upsellResize?.disconnect(), { once: true });
      }

      this.updateUpsellControls();
    }

    /** One press moves by a whole view, so the snap lands on the next pair. */
    upsellPageWidth(track) {
      const card = track.querySelector('.upsell-product-item:not(.is-hidden)');
      if (!card) return track.clientWidth;

      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return (card.getBoundingClientRect().width + gap) * 2;
    }

    updateUpsellControls() {
      const wrapper = document.querySelector('.cart-upsell-wrapper');
      const track = wrapper?.querySelector('[data-upsell-track]');
      const controls = wrapper?.querySelector('[data-upsell-controls]');
      if (!track || !controls) return;

      // Sub-pixel track widths round up, so a strip that fits can report one
      // stray pixel of overflow; a card is never that narrow.
      const maxScroll = track.scrollWidth - track.clientWidth;
      controls.hidden = maxScroll <= 2;
      if (controls.hidden) return;

      const prev = controls.querySelector('[data-upsell-prev]');
      const next = controls.querySelector('[data-upsell-next]');
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
    }

    handleUpsellAdd(button) {
      const variantId = Number(button.dataset.id);
      if (!variantId) return;

      // Busy, not disabled — a disabled button drops the next tap on the floor.
      // The guard is what keeps one press to one add; see handleSetPromo.
      if (button.getAttribute('aria-busy') === 'true') return;

      button.setAttribute('aria-busy', 'true');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');

      this.addToCart([{ id: variantId, quantity: 1 }]).finally(() => {
        button.removeAttribute('aria-busy');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      });
    }

    onUpsellVariantChange(select) {
      const card = select.closest('.upsell-product-item');
      const option = select.options[select.selectedIndex];
      if (!card || !option) return;

      const variantId = option.value;
      const available = option.dataset.available === 'true';

      card.dataset.variantId = variantId;

      const button = card.querySelector('.js-upsell-atc');
      if (button) {
        button.dataset.id = variantId;
        button.disabled = !available;
        const label = button.querySelector('.mm-btn__label');
        if (label) {
          label.textContent = available
            ? button.dataset.addLabel || 'Add to cart'
            : button.dataset.soldoutLabel || 'Sold out';
        }
      }

      const image = card.querySelector('.upsell-product-item__image');
      if (image && option.dataset.image) image.src = option.dataset.image;
    }

    filterUpsellProducts(cart) {
      const wrapper = document.querySelector('.cart-upsell-wrapper');
      if (!wrapper || !cart) return;

      const cartVariantIds = new Set((cart.items || []).map((item) => item.variant_id));
      let visible = 0;

      wrapper.querySelectorAll('.upsell-product-item').forEach((card) => {
        const ids = this.upsellVariantIds(card);
        const inCart = ids.some((id) => cartVariantIds.has(id));
        card.classList.toggle('is-hidden', inCart);
        if (!inCart) visible += 1;
      });

      wrapper.classList.toggle('hidden', visible === 0);
      this.updateUpsellControls();
    }

    upsellVariantIds(card) {
      const manifest = this._upsellManifest ?? (this._upsellManifest = this.readUpsellManifest());
      const productId = Number(card.dataset.productId);
      const entry = manifest.find((item) => item.product_id === productId);

      if (entry?.variant_ids?.length) return entry.variant_ids.map(Number);
      return [Number(card.dataset.variantId)];
    }

    readUpsellManifest() {
      const node = document.querySelector('.js-upsell-products-json');
      if (!node) return [];

      try {
        return JSON.parse(node.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    /* ---------------------------------------------------------- adding */

    addToCart(items, options = {}) {
      if (window.mmCartAdd?.add) return window.mmCartAdd.add(items, options);

      return fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
        ...theme.utils.fetchConfig(),
        body: JSON.stringify({ items, sections: [this.sectionId] }),
      })
        .then((response) => response.json())
        .then((parsed) => {
          this.renderSections(parsed.sections);
          return this.getCart(true).then((cart) => {
            this.schedulePostCartUpdateWork(true, cart);
            return { ok: true, cart };
          });
        })
        .catch((error) => {
          console.error('[mm-cart] add failed', error);
          this.showCartError();
          return { ok: false, error };
        });
    }

    /* --------------------------------------------------- progress bar */

    updateProgressBar(cartJson, announce) {
      const root = document.querySelector('#bxgy-progress');
      if (!root || !cartJson) return;

      const items = cartJson.items || [];

      // 1. Only lines that are actually free are excluded. A "gift" the shopper
      //    is paying for still counts toward the total.
      const giftItems = items.filter(
        (item) => item.properties?._progressbar_promotion && item.final_line_price === 0
      );
      const giftCount = giftItems.reduce((sum, item) => sum + item.quantity, 0);

      const tiers = this.readTiers(root);
      if (tiers.length === 0) return;

      // 2. Measured values. Each price milestone can pick its own basis (the
      //    block's setting, falling back to the drawer's), so each basis is
      //    measured once and shared by the milestones that use it.
      const values = new Map();
      const valueFor = (tierBasis) => {
        if (!values.has(tierBasis)) {
          values.set(tierBasis, this.measureCartValue(tierBasis, cartJson, tiers, giftItems));
        }
        return values.get(tierBasis);
      };

      const cartItemCount = Math.max((cartJson.item_count || 0) - giftCount, 0);

      // 5. Prior state, so we can tell "newly reached" from "still reached".
      tiers.forEach((tier) => {
        tier.wasReached =
          this.reachedRewardKeys.has(tier.key) || this.isRewardFulfilledInCart(tier, cartJson);
      });

      // 6. Visual positions.
      this.measureTierPositions(root, tiers);
      tiers.sort((a, b) => a.visualPosition - b.visualPosition);

      // 7. Reached state, reward queues.
      const giftsToAdd = [];
      const codesToAdd = [];
      const codesToRemove = [];
      let nextTier = null;
      const newlyReached = [];

      tiers.forEach((tier) => {
        tier.current = tier.thresholdType === 'items' ? cartItemCount : valueFor(tier.basis);
        tier.isReached = tier.current >= tier.threshold;
        tier.element.classList.toggle('reached', tier.isReached);

        if (tier.isReached && !tier.wasReached) {
          newlyReached.push(tier);
          this.reachedRewardKeys.add(tier.key);

          const variantId = this.resolveGiftVariant(tier, cartJson);
          if (variantId && !this.pendingPromoAdds.has(variantId)) {
            this.pendingPromoAdds.add(variantId);
            giftsToAdd.push(variantId);
          }
          if (tier.discountCode) codesToAdd.push(tier.discountCode);
        } else if (!tier.isReached && tier.wasReached) {
          this.reachedRewardKeys.delete(tier.key);
          this.removeGiftProduct(tier, cartJson);
          if (tier.discountCode) codesToRemove.push(tier.discountCode);
        }

        if (tier.isReached) this.applyRewardLabel(tier, cartJson);
        if (!tier.isReached && !nextTier) nextTier = tier;
      });

      // 8. One request for however many rewards a single threshold unlocked.
      if (giftsToAdd.length > 0) this.addRewardProducts(giftsToAdd);
      if (codesToAdd.length > 0 || codesToRemove.length > 0) {
        this.syncDiscountCodes(cartJson, codesToAdd, codesToRemove);
      }

      // 11. Toast for the highest newly reached tier.
      if (announce && newlyReached.length > 0) {
        const top = newlyReached[newlyReached.length - 1];
        this.showRewardMessage(top.discountMessage || 'Reward unlocked!');
      }

      // 12. Fill.
      this.paintFill(tiers);
    }

    /**
     * The cart's value in major units under one basis:
     * - subtotal: every line at full price, free reward gifts left out.
     * - discounted: what the customer pays, with the bar's own reward discounts
     *   added back.
     * - line_items: each line's own price after its item-level discounts
     *   (quantity tiers and the like), summed line by line. Order-wide discounts
     *   are left out, so a "% off orders over $X" doesn't pull the cart back
     *   under its milestone. Free gifts are skipped via their 0 price.
     */
    measureCartValue(basis, cartJson, tiers, giftItems) {
      const items = cartJson.items || [];
      let value;

      if (basis === 'discounted') {
        value = cartJson.total_price + this.getRewardDiscountTotal(cartJson, tiers);
      } else if (basis === 'line_items') {
        value =
          items.reduce((sum, item) => sum + (item.final_line_price || 0), 0) +
          this.getRewardDiscountTotal(cartJson, tiers, { lineLevelOnly: true });
      } else {
        const giftOriginal = giftItems.reduce((sum, item) => sum + item.original_line_price, 0);
        value = cartJson.original_total_price - giftOriginal;
      }

      return Math.max(value, 0) / 100;
    }

    readTiers(root) {
      return Array.from(root.querySelectorAll('.js-promotion-item'))
        .map((element) => {
          const threshold = parseFloat(element.dataset.threshold);
          if (!Number.isFinite(threshold) || threshold <= 0) return null;

          const thresholdType = element.dataset.thresholdType || 'price';
          // No block basis means "same as the drawer setting".
          const basis = element.dataset.priceBasis || root.dataset.progressBasis || 'subtotal';
          const productId = element.dataset.productId || '';
          const discountCode = (element.dataset.discountCode || '').trim();

          return {
            element,
            threshold,
            thresholdType,
            basis,
            productId,
            discountCode,
            discountMessage: element.dataset.discountMessage || '',
            giftManuka: element.dataset.giftVariantManuka || '',
            giftManukaName: element.dataset.giftVariantManukaName || '',
            giftFire: element.dataset.giftVariantFire || '',
            giftFireName: element.dataset.giftVariantFireName || '',
            key: `${thresholdType}:${threshold}:${productId}:${discountCode}`,
            visualPosition: 0,
          };
        })
        .filter(Boolean);
    }

    /**
     * Where each milestone's icon sits on the track, in % of the track width:
     * its centre (`visualPosition`, used for ordering) and its left/right edges
     * (`visualStart`/`visualEnd`), which bound the visible stretch of track the
     * fill travels between milestones.
     */
    measureTierPositions(root, tiers) {
      const track = root.querySelector('.progress-line');
      const trackRect = track?.getBoundingClientRect();

      tiers.forEach((tier, index) => {
        const icon = tier.element.querySelector('.circle-image') || tier.element;
        const rect = icon.getBoundingClientRect();

        if (!trackRect || trackRect.width === 0 || rect.width === 0) {
          // The drawer is closed and everything measures 0 — fall back to an
          // even spread so the fill is still roughly right on first paint.
          tier.visualPosition = ((index + 0.5) / tiers.length) * 100;
          tier.visualStart = tier.visualPosition;
          tier.visualEnd = tier.visualPosition;
          return;
        }

        const toPercent = (x) => ((x - trackRect.left) / trackRect.width) * 100;
        tier.visualPosition = toPercent(rect.left + rect.width / 2);
        tier.visualStart = toPercent(rect.left);
        tier.visualEnd = toPercent(rect.right);
      });
    }

    /**
     * Adds back the discounts this bar granted itself. Without this a reward
     * discount pushes the cart under the threshold that unlocked it, and the
     * tier oscillates on every update. `lineLevelOnly` is for the line-items
     * basis, which never subtracted the cart-level ones in the first place.
     */
    getRewardDiscountTotal(cartJson, tiers, { lineLevelOnly = false } = {}) {
      const rewardCodes = new Set(
        [...tiers.map((tier) => tier.discountCode), ...this.appliedRewardDiscounts]
          .filter(Boolean)
          .map((code) => code.toLowerCase().trim())
      );
      if (rewardCodes.size === 0) return 0;

      const matches = (title) => rewardCodes.has(String(title || '').toLowerCase().trim());

      let total = 0;

      if (!lineLevelOnly) {
        (cartJson.cart_level_discount_applications || []).forEach((application) => {
          if (matches(application.title)) total += application.total_allocated_amount;
        });
      }

      (cartJson.items || []).forEach((item) => {
        if (item.properties?._progressbar_promotion) return;
        (item.line_level_discount_allocations || []).forEach((allocation) => {
          if (matches(allocation.discount_application?.title)) total += allocation.amount;
        });
      });

      return total;
    }

    isRewardFulfilledInCart(tier, cartJson) {
      if (tier.productId) {
        const variantId = Number(tier.productId);
        const present = (cartJson.items || []).some(
          (item) => item.variant_id === variantId && item.properties?._progressbar_promotion
        );
        if (present) return true;
      }

      if (tier.discountCode) {
        return (cartJson.discount_codes || []).some(
          (entry) =>
            String(entry.code).toLowerCase() === tier.discountCode.toLowerCase() &&
            entry.applicable !== false
        );
      }

      return false;
    }

    /**
     * Gives the opposite scent to whatever is already in the cart: Manuka in,
     * Fire out — and vice versa.
     */
    detectCartScents(cartJson) {
      const scents = { fire: false, manuka: false };

      (cartJson.items || []).forEach((item) => {
        if (item.properties?._progressbar_promotion) return;

        const haystack = [
          item.variant_title,
          (item.variant_options || []).join(' '),
          (item.options_with_values || []).map((option) => option.value).join(' '),
        ]
          .join(' ')
          .toLowerCase();

        if (haystack.includes('fire') || haystack.includes('spice')) scents.fire = true;
        if (haystack.includes('manuka')) scents.manuka = true;
      });

      return scents;
    }

    resolveGiftVariant(tier, cartJson) {
      if (!tier.productId && !tier.giftManuka && !tier.giftFire) return null;

      const scents = this.detectCartScents(cartJson);

      if (scents.manuka && !scents.fire && tier.giftFire) return tier.giftFire;
      if (scents.fire && !scents.manuka && tier.giftManuka) return tier.giftManuka;

      return tier.giftManuka || tier.giftFire || tier.productId || null;
    }

    applyRewardLabel(tier, cartJson) {
      const reward = tier.element.querySelector('.pro-reward');
      if (!reward) return;

      const variantId = this.resolveGiftVariant(tier, cartJson);
      if (!variantId) return;

      let name = '';
      let scent = '';
      if (String(variantId) === String(tier.giftFire)) {
        name = tier.giftFireName;
        scent = 'fire';
      } else if (String(variantId) === String(tier.giftManuka)) {
        name = tier.giftManukaName;
        scent = 'manuka';
      }
      if (!name) return;

      let span = reward.querySelector('.pro-reward__variant');
      if (!span) {
        span = document.createElement('span');
        span.className = 'pro-reward__variant';
        reward.appendChild(span);
      }

      span.textContent = name;
      span.classList.remove('pro-reward__variant--fire', 'pro-reward__variant--manuka');
      if (scent) span.classList.add(`pro-reward__variant--${scent}`);
    }

    addRewardProducts(variantIds) {
      const items = variantIds.map((id) => ({
        id: Number(id),
        quantity: 1,
        properties: { _progressbar_promotion: 'true' },
      }));

      // The drawer is already open showing the reward — it should not slide in
      // again on top of itself.
      return this.addToCart(items, { openDrawer: false }).finally(() => {
        variantIds.forEach((id) => this.pendingPromoAdds.delete(id));
      });
    }

    removeGiftProduct(tier, cartJson) {
      const variantIds = [tier.productId, tier.giftFire, tier.giftManuka].filter(Boolean).map(Number);
      const line = (cartJson.items || []).find(
        (item) => item.properties?._progressbar_promotion && variantIds.includes(item.variant_id)
      );
      if (!line) return;

      fetch(`${cartChangeUrl}.js`, {
        ...theme.utils.fetchConfig(),
        body: JSON.stringify({
          id: line.key,
          quantity: 0,
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((parsed) => {
          this.renderSections(parsed.sections);
          this._latestCart = parsed;
        })
        .catch((error) => console.error('[mm-cart] gift removal failed', error));
    }

    /* --------------------------------------------- quantity tier discounts */

    /**
     * A tier's discount code lives as long as the line it came in with. When
     * that line is removed here — or its quantity is zeroed, or checkout empties
     * the cart — the code has to come off too, or the next order still gets it.
     *
     * Only codes listed in the cart attribute are eligible: a code the shopper
     * typed, or one the rewards bar granted, is never touched.
     */
    reconcileTierDiscounts(cart) {
      if (!cart) return;

      const owned = this.readTierDiscountAttribute(cart);
      if (owned.length === 0) return;

      const backed = new Set(
        (cart.items || [])
          .map((item) => String(item.properties?.[TIER_DISCOUNT_PROPERTY] || '').trim().toLowerCase())
          .filter(Boolean)
      );

      const stale = owned.filter((code) => !backed.has(code.toLowerCase()));
      if (stale.length === 0) return;

      const keep = owned.filter((code) => backed.has(code.toLowerCase()));
      this.syncDiscountCodes(cart, [], stale, { [TIER_DISCOUNT_ATTRIBUTE]: keep.join(',') });
    }

    readTierDiscountAttribute(cart) {
      return String(cart?.attributes?.[TIER_DISCOUNT_ATTRIBUTE] || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }

    /**
     * Always POSTs the rebuilt list. Shopify retains inapplicable codes in
     * session storage and silently reapplies them if you only "add".
     */
    syncDiscountCodes(cartJson, adds, removes, attributes) {
      const codes = this.buildDiscountSet(cartJson, adds, removes);

      adds.forEach((code) => this.appliedRewardDiscounts.add(code));
      removes.forEach((code) => this.appliedRewardDiscounts.delete(code));

      fetch(`${cartUpdateUrl}.js`, {
        ...theme.utils.fetchConfig(),
        body: JSON.stringify(
          attributes ? { discount: codes.join(','), attributes } : { discount: codes.join(',') }
        ),
      })
        .then(() => this.getCart(true))
        .then((cart) => {
          if (cart) {
            this._latestCart = cart;
            this.updateProgressBar(cart, false);
          }
        })
        .catch((error) => console.error('[mm-cart] discount sync failed', error));

      // Belt and braces: the /discount/ endpoint makes the code survive to checkout.
      adds.forEach((code) => {
        fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET' }).catch(() => {});
      });
    }

    buildDiscountSet(cartJson, adds, removes) {
      const removeSet = new Set(removes.map((code) => code.toLowerCase()));
      const result = new Map();

      const push = (code) => {
        if (!code) return;
        const key = code.toLowerCase();
        if (removeSet.has(key)) return;
        if (!result.has(key)) result.set(key, code);
      };

      (cartJson.discount_codes || []).forEach((entry) => push(entry.code));
      this.appliedRewardDiscounts.forEach(push);
      adds.forEach(push);

      return Array.from(result.values());
    }

    showRewardMessage(message) {
      const toast = document.querySelector('#bxgy-progress-message');
      if (!toast) return;

      const text = toast.querySelector('.bxgy-progress-message__text');
      if (text) text.textContent = message;

      toast.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => toast.classList.remove('is-visible'), REWARD_TOAST_MS);
    }

    /**
     * The fill moves in proportion to the amount: across the visible track
     * between the last reached icon and the next one, from the previous
     * threshold to the next. It meets the next icon's edge exactly at its
     * threshold, and the part under the icons is only covered once reached,
     * so the fill neither crawls nor jumps.
     */
    paintFill(tiers) {
      const fill = document.querySelector('#progressFill');
      if (!fill) return;

      const index = tiers.findIndex((tier) => !tier.isReached);
      if (index === -1) {
        fill.style.width = '100%';
        return;
      }

      const tier = tiers[index];
      const previous = index > 0 ? tiers[index - 1] : null;

      // Measured on the next milestone's own basis. The previous threshold is a
      // starting point only when it counts the same kind of thing (an amount or
      // an item count); otherwise progress runs from zero.
      const from = previous && previous.thresholdType === tier.thresholdType ? previous.threshold : 0;
      const span = tier.threshold - from;
      let ratio = span > 0 ? (tier.current - from) / span : 0;
      ratio = Math.min(Math.max(ratio, 0), 1);

      const start = previous ? previous.visualEnd : 0;
      const end = Math.max(start, tier.visualStart);
      const percent = start + ratio * (end - start);

      fill.style.width = `${Math.min(Math.max(percent, 0), 100)}%`;
    }
  }

  if (!customElements.get('mm-cart-remove-button')) {
    customElements.define('mm-cart-remove-button', MMCartRemoveButton);
  }
  if (!customElements.get('mm-quantity-input')) {
    customElements.define('mm-quantity-input', MMQuantityInput);
  }
  if (!customElements.get('mm-cart-items')) {
    customElements.define('mm-cart-items', MMCartItems);
  }
})();
